import {
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/20/solid'
import { useRugcheck } from 'hooks/useRugcheck'
import { useMemo, memo } from 'react'
import Tooltip from '../shared/Tooltip'
import SheenLoader from '../loading/SheenLoader'
import { useTranslation } from 'next-i18next'
import store from 'stores/store'
import { ExternalLink } from '@components/shared/ExternalLink'

const RugcheckLevel = () => {
  const baseMint = store((s) => s.market.baseToken.mint)
  const { data: rugcheckData, isInitialLoading: loadingRugcheck } =
    useRugcheck(baseMint)

  const { level, risks } = useMemo(() => {
    if (!rugcheckData) {
      return { level: 'none', risks: [] } as const
    }
    if (!rugcheckData.risks?.length) {
      return { level: 'okay', risks: [] } as const
    }

    const isDanger = rugcheckData.risks.find((data) => data.level === 'danger')
    const rugcheckUniqueRisksMap = new Map<string, string>()
    rugcheckData.risks.forEach(({ name, description }) => {
      // we only want the first instance of a duplicate risk, they seem to be sorted by severity
      if (!rugcheckUniqueRisksMap.has(name)) {
        rugcheckUniqueRisksMap.set(name, description)
      }
    })
    const rugcheckUniqueRisks = [...rugcheckUniqueRisksMap].map(
      ([name, description]) => ({
        name,
        description,
      }),
    )
    return {
      level: isDanger ? 'danger' : 'warning',
      risks: rugcheckUniqueRisks,
    } as const
  }, [rugcheckData])

  return (
    <>
      {loadingRugcheck ? (
        <SheenLoader>
          <div className="h-5 w-12 bg-th-bkg-2" />
        </SheenLoader>
      ) : level === 'none' ? (
        <QuestionMarkCircleIcon className="size-4 text-th-fgd-4" />
      ) : level === 'okay' ? (
        <RugcheckTooltip mint={baseMint} riskLevel="okay" />
      ) : (
        <RugcheckTooltip mint={baseMint} riskLevel={level} risks={risks} />
      )}
    </>
  )
}

export default RugcheckLevel

type RugcheckTooltipProps =
  | {
      mint: string
      riskLevel: 'okay'
    }
  | {
      mint: string
      riskLevel: 'danger' | 'warning'
      risks: {
        name: string
        description: string
      }[]
    }

const RugcheckTooltip = memo(function RugcheckTooltip(
  props: RugcheckTooltipProps,
) {
  const { mint, riskLevel } = props
  const { t } = useTranslation('token-page')

  const { textStyle, label, icon } =
    riskLevel === 'okay'
      ? ({
          icon: <ShieldCheckIcon className="size-4 text-th-up" />,
          textStyle: 'text-th-success',
          label: 'rugcheck.okay',
        } as const)
      : riskLevel === 'warning'
        ? ({
            icon: (
              <ExclamationTriangleIcon className="size-4 text-th-warning" />
            ),
            textStyle: 'text-xs text-th-warning',
            label: 'rugcheck.warning',
          } as const)
        : ({
            icon: <FlagIcon className="size-4 text-th-down" />,
            textStyle: 'text-xs text-th-down',
            label: 'rugcheck.danger',
          } as const)

  return (
    <Tooltip
      content={
        <div className="space-y-2">
          {riskLevel !== 'okay'
            ? props.risks.map((risk) => (
                <div key={risk.name}>
                  <p className="mb-0.5 text-th-fgd-1">{risk.name}</p>
                  <p>{risk.description}</p>
                </div>
              ))
            : null}
          <ExternalLink
            className="flex items-center text-th-fgd-2 md:hover:text-th-fgd-2"
            href={`https://rugcheck.xyz/tokens/${encodeURIComponent(mint)}`}
          >
            <span className="mr-1">{t('rugcheck.powered-by')}</span>
            <ArrowTopRightOnSquareIcon className="size-3.5" />
          </ExternalLink>
        </div>
      }
    >
      <div className="flex cursor-help items-center space-x-1">
        {icon}
        <span className={textStyle}>{t(label)}</span>
      </div>
    </Tooltip>
  )
})
